import React, { useState, useEffect, useMemo } from 'react';
import sortBy from 'lodash.sortby';
import debounce from 'lodash.debounce';
import { group, extent } from 'd3-array';
import { color as d3Color } from 'd3-color';
import { scaleOrdinal } from 'd3-scale';
import { interpolateTurbo } from 'd3-scale-chromatic';

import Sankey from './Sankey';
import Emissions from './Emissions';
import Table from '../../Table';
import Tooltip from '../../Tooltip/';

import Localization from '../../Localization';
import { exportSVGAsPDF } from '../../exportViz';
import mapToNest from '../../mapToNest.js';
import { geoKey, colors, formatAs } from '../../settings.js';
import BULogo from '../../images/bu-logo.js';
// import GDPCLogo from '../../images/GDP.Logo.Finalbright.png';
import GDPCLogoInverted from '../../images/BU-GDPC-logo-inverted.png';
import GDPCLogo from '../../images/BU-GDPC-logo.png';
import Text from 'react-svg-text'

import './styles.scss';

let svg = null;

const geoColors = scaleOrdinal();

export const metrics = {
  width: 1280,
  emissionsHeight: 640,
  headerHeight: 90,
  sectionHeight: 160,
  labelHeight: 40,
  barWidth: 16,
  font: {
    xlarge: 40,
    large: 32,
    sankeyTitle: 24,
    medium: 20,
    emCountries: 14.4,
    small: 12,
  },
  padding: 10,
  margin: {
    small: 60,
    large: 300,
  },
  tooltip: {
    width: 224,
    height: 136,
  }
};
metrics.columnWidth = (metrics.width - (metrics.margin.large * 2)) / 2;
metrics.emissionsInnerWidth = metrics.width - metrics.margin.small * 5;
metrics.emissionsInnerHeight = metrics.emissionsHeight - metrics.margin.small * 3.5;

export function recursiveNest(data, layers, index, filter = true) {
  const layer = layers[index];
  return sortBy(mapToNest(group(data, layer.key)).map(d => {
    return {
      name: d.key,
      color: colors[d.key] ? colors[d.key].light : 'rgba(255, 255, 255, 0.25)',
      value: d.values.map(d => d[layer.value]).reduce((a, v) => a + v, 0),
      groups: (index < layers.length - 1)
        ? recursiveNest(d.values, layers, index + 1)
        : sortBy(d.values, v => -v[layer.value]),
    };
  }), d => -d.value).filter(d => filter ? d.value > 0 : true);
}

export default function Report(props) {
  const [width, setWidth] = useState(document.body.clientWidth);
  const [valueKey, setValueKey] = useState('co2emissions');
  const [sankeyHeight, setSankeyHeight] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [tableTooltip, setTableTooltip] = useState(null);
  const [emissionsTooltip, setEmissionsTooltip] = useState(null);

  const { data, geoType, selected, setReport } = props;

  useEffect(() => {
    function _measure() {
      setWidth(document.body.clientWidth);
    }
    _measure();
    const debouncedMeasure = debounce(_measure, 100, { leading: true, trailing: true });
    window.addEventListener('resize', debouncedMeasure);
    return function cleanup() {
      window.removeEventListener('resize', debouncedMeasure);
    }
  }, []);

  // Filter data by selected geos
  const investments = useMemo(() => (
    data.investments.filter(d => selected.includes(geoKey[geoType](d))).map(d => d.units).flat()
  ), [data.investments, selected, geoType]);
  const years = useMemo(() => [...new Set(investments.map(d => d.year).concat(2030))], [investments]);
  const yearExtent = extent(years);

  const geoSelection = geoType === 'regions' && data.regions.length === selected.length ? [''] : selected;

  const geoColorRange = useMemo(() => (
    selected.map((s, i) => d3Color(interpolateTurbo((selected.length - i) / selected.length)).darker(0.5))
  ), [selected]);
  geoColors.range(geoColorRange);

  const summary = useMemo(() => mapToNest(group(investments, d => 'capacity'))
    .map(d => {
      const geos = sortBy(mapToNest(group(d.values, d => geoKey[geoType](d)))
        .map(g => {
          const projects = sortBy(g.values.map(project => {
            project.name = project.plant;
            return project;
          }), project => project[valueKey] === 'TBD' ? 3000 : project[valueKey]).reverse();
          const [year] = sortBy(projects.map(p => p.year), y => -y);
          const name = geoType === 'states' ? projects[0].state : g.key;
          return {
            name,
            capacity: g.values.map(v => v.capacity).reduce((a, v) => a + v, 0),
            co2emissions: g.values.map(v => v.co2emissions).reduce((a, v) => a + v, 0),
            year,
            projects,
          };
      }), g => g.hasOwnProperty(valueKey) ? g[valueKey] === 'TBD' ? 3000 : g[valueKey] : g['co2emissions']).reverse();
      const geoColorDomain = sortBy(geos.map(g => g.co2emissions), v => -v);
      geoColors.domain(geoColorDomain);
      const [year] = sortBy(geos.map(g => g.year), y => y === 'TBD' ? 3000 : -y);
      return {
        name: d.key,
        capacity: d.values.map(v => v.capacity).reduce((a, v) => a + v, 0),
        co2emissions: d.values.map(v => v.co2emissions).reduce((a, v) => a + v, 0),
        value: d.values.map(v => v[valueKey]).reduce((a, v) => a + v, 0),
        fill: colors[d.key] ? colors[d.key].light : '#C00',
        groupFill: colors[d.key] ? colors[d.key].transparent : '#E5E5E5',
        geos: geos.map(g => {
          g.fill = geoColors(g.co2emissions);
          return g;
        }),
        year,
      };
    })
  , [investments, valueKey, geoType]);

  const height = sankeyHeight + metrics.emissionsHeight;
  const scale = width / metrics.width;

  const initiateExport = () => {
    setExporting(true)
  }

  const tableMarginTop = (height * scale) - height;
  let reportHeader = null
  let reportHeaderHeight = 0
  if (exporting) {
    reportHeaderHeight = 67 + 70
    reportHeader = (
      <g>
        <rect fill='#cc0000' height='67' width={metrics.width} />
        <g transform={`translate(100, ${(67 - 27)/2})`}>
          <BULogo />
          <text fill='#fff' x={50} y={27 / 2 + 4}>Chinese Global Power Projects + Emissions</text>
        </g>
        <rect y={67} height='70' fill='#fff' width={metrics.width} />
        <text y={97} x={150} fill={'#4C4C4C'}>
          {Localization.get('POWER GENERATION CAPACITY AND ESTIMATED ANNUAL CO2 EMISSION WITH CHINESE CAPITAL BY 2033')}
        </text>
        <Text verticalAnchor='middle' y={112} x={150} width={metrics.width - 150 * 3} fill='#cc0000'>
          {props.selected.map(name => Localization.get(name.toUpperCase())).join(', ')}
        </Text>
      </g>
    )
  }

  useEffect(() => {
    if (exporting) {
      exportSVGAsPDF(svg, 'report', [metrics.width, height + reportHeaderHeight])
      setExporting(false)
    }
  }, [exporting, height, reportHeaderHeight])

  return (
    <div className='Report'>
      <div className='options'>
        <div className='settings'>
          <div className='metrics'>
            {
              // Power Generation Capacity and Estimated Annual CO<sub>2</sub> Emission with Chinese Capital by 2030
              // todo: use subscript for CO2
            }
            {Localization.get('POWER GENERATION CAPACITY AND ESTIMATED ANNUAL CO2 EMISSION WITH CHINESE CAPITAL BY 2033')}
          </div>
          <div className='geos'>{props.selected.map(g => Localization.get(g.toUpperCase())).join(', ')}</div>
        </div>
        <div className='controls'>
          <div className='button' onClick={() => setReport(false)}>
            <button>
              {Localization.get('Modify Report')}
            </button>
          </div>
          <div className='button' onClick={initiateExport}>
            <button>
              {Localization.get('Export PDF')}
            </button>
          </div>
        </div>
      </div>
      <div className='svgContainer' style={{ height: height, transform: `scale(${scale})`, width: metrics.width }}>
        <svg
          ref={r => svg = r}
          width={metrics.width}
          height={height + reportHeaderHeight}
          fill='white'
          fontWeight={exporting ? "normal" : "bold"}
          fontFamily={exporting ? "NotoSansCJKtc-Bold" : "'Source Sans Pro', sans-serif"}
        >
          {reportHeader}
          <g transform={`translate(0,${reportHeaderHeight})`}>
            <Sankey
              investments={investments}
              geoType={geoType}
              yearExtent={yearExtent}
              setSankeyHeight={setSankeyHeight}
              language={props.language}
            />
            <Emissions
              investments={investments}
              scale={scale}
              geoType={geoType}
              geoColors={geoColors}
              yearExtent={yearExtent}
              sankeyHeight={sankeyHeight}
              tooltipData={emissionsTooltip}
              setTooltipData={setEmissionsTooltip}
            />
          </g>
          {
            exporting ? (
              <g transform={`translate(0, ${reportHeaderHeight})`}>
                <g transform={`translate(${metrics.width - (metrics.padding + metrics.margin.large)}, ${sankeyHeight - (metrics.padding + metrics.margin.large / 10)})`}>
                  <image href={GDPCLogoInverted} width={metrics.margin.large} height={metrics.margin.large / 10} />
                </g>
                <g transform={`translate(${metrics.width - (metrics.padding + metrics.margin.large)}, ${sankeyHeight + metrics.emissionsHeight - (metrics.padding + metrics.margin.large / 10)})`}>
                  <image href={GDPCLogo} width={metrics.margin.large} height={metrics.margin.large / 10} />
                </g>
              </g>
            ) : null
          }
        </svg>
        {
          emissionsTooltip ? (
            <div className='tooltip' style={emissionsTooltip.style}>
              <div className='title' style={{ color: emissionsTooltip.color }}>
                {`${Localization.get(emissionsTooltip.name.toUpperCase())}, ${emissionsTooltip.year}`}
              </div>
              <div className='row header'>
                <div className='name'>{Localization.get('Project')}</div>
                <div className='value'>{`${Localization.get('Emissions')} (${Localization.get('K Ton')})`}</div>
              </div>
              {emissionsTooltip.projects.map(p => (
                <div key={p.name} className='row'>
                  <div className='name'>{p.name}</div>
                  <div className='value'>{formatAs.number(p.value)}</div>
                </div>
              ))}
              <div className='row total'>
                <div className='name'>{emissionsTooltip.year} {Localization.get('Total')}</div>
                <div className='value'>{formatAs.number(emissionsTooltip.total)}</div>
              </div>
              <div className='row total'>
                <div className='name'>{Localization.get('Cumulative Total')}</div>
                <div className='value'>{formatAs.number(emissionsTooltip.cumulativeEmissions)}</div>
              </div>
            </div>
          ) : null
        }
      </div>
      <div className='tableContainer' style={{ marginTop: tableMarginTop }}>
        <Table
          data={summary}
          valueKey={valueKey}
          setValueKey={setValueKey}
          isCapacity={true}
          geoSelection={geoSelection}
          selectedYears={data.years}
          allYears={data.years}
          setTooltipData={setTableTooltip}
          lockable={false}
        />
      </div>
      {tableTooltip ? (
        <Tooltip
          {...tableTooltip}
          width={document.body.clientWidth}
          height={document.body.clientHeight}
        />
      ) : null}
    </div>
  )
}